import { Theme } from '@mui/material';
import { GeoJSONSource, GeoJSONSourceRaw } from '@trimblemaps/trimblemaps-js';

import { Lane } from '../../../interfaces/services/lanes';
import { MapArea } from '../../../interfaces/services/geography';
import { MapSources } from '../enums';
import { LanesMapSourceProperties, determineLanesSource } from './lanes';
import { AreasMapSourceProperties, determineAreasSource } from './areas';

/** Interface used for the properties object on the Lane Details Map Source. */
export interface LaneDetailsMapSourceProperties extends LanesMapSourceProperties, AreasMapSourceProperties {
}

/**
 * Loop through the lanes and areas and add a geometry feature to the source for each area location.
 * Adds the lane details source to the map if it doesn't already exist
 * @param map Instance of your map
 * @param lanes The lanes to show on the map
 * @param areas The areas to show on the map
 * @param theme MUI theme to use for styling
 * @returns A geojson source to be added to the map
*/
export const addLaneDetailsSource = ({
    map,
    lanes,
    areas,
    areaNames,
    theme,
}: {
    map: TrimbleMaps.Map;
    lanes: Lane[];
    areas: MapArea[];
    areaNames: string[];
    theme: Theme;
}): GeoJSONSourceRaw => {
    const laneFeatures = determineLanesSource({
        lanes,
        hoveredLaneId: null,
        theme,
    });

    const areaFeatures = determineAreasSource({
        areas,
        areaNames,
        theme,
    });

    const source: GeoJSONSourceRaw = {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features: [...laneFeatures, ...areaFeatures],
        },
        generateId: true,
    };

    if (map.getSource(MapSources.laneDetailsSource) === undefined) {
        map.addSource(MapSources.laneDetailsSource, source);
    } else if (source.data) {
        (map.getSource(MapSources.laneDetailsSource) as GeoJSONSource).setData(source.data);
    }

    return source;
};
